import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Loader } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { getPushState, refreshPushRegistration, isPushReady } from '../../lib/push';
import { useLanguage } from '../../hooks/useLanguage';

// Asks the driver to allow ride-request notifications. Shows on every app open /
// resume while push isn't registered yet - a driver who skipped the OS prompt the
// first time (and so never received any ride pushes) keeps getting re-asked until
// it's on. Once registered, the Profile → Notifications card reflects it as on.
const EnableNotificationsPrompt: React.FC = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [denied, setDenied] = useState(false);

  // Back off after the user dismisses / when enabling can't succeed (e.g. iOS
  // builds without a working push entitlement) so the prompt never feels like an
  // endless loop. We still re-ask, just not on every single reopen.
  const SNOOZE_KEY = 'pushPromptSnoozeUntil';
  const SNOOZE_MS = 24 * 60 * 60 * 1000;
  const snooze = () => localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  const isSnoozed = () => Date.now() < Number(localStorage.getItem(SNOOZE_KEY) || '0');

  // Only meaningful inside the installed native app - web/PWA can't receive
  // ride pushes, so never nag there. Checks the LIVE OS permission (not the
  // cached flag) so disabling notifications in Settings re-triggers the prompt.
  const reevaluate = useCallback(async () => {
    if (!Capacitor.isNativePlatform() || isSnoozed()) { setOpen(false); return; }
    const ready = await isPushReady();
    if (!ready) { setDenied(false); setOpen(true); }
    else setOpen(false);
  }, []);

  // Re-ask on first mount and every time the app comes back to the foreground.
  useEffect(() => {
    reevaluate();
    const onVisible = () => { if (document.visibilityState === 'visible') reevaluate(); };
    document.addEventListener('visibilitychange', onVisible);
    let remove: (() => void) | undefined;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('resume', reevaluate);
        remove = () => handle.remove();
      } catch {}
    })();
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      remove?.();
    };
  }, [reevaluate]);

  const enable = async () => {
    setEnabling(true);
    setDenied(false);
    try { await refreshPushRegistration(); } catch {}
    // Registration resolves asynchronously (OS prompt + token round-trip).
    setTimeout(async () => {
      setEnabling(false);
      // Hide as soon as the OS permission is granted - don't wait on the token
      // (iOS APNs→FCM can lag), otherwise the prompt would re-show in a loop.
      if (await isPushReady()) { localStorage.removeItem('pushPromptSnoozeUntil'); setOpen(false); return; }
      if (getPushState() === 'denied') { setDenied(true); return; } // blocked at OS level - guide to Settings
      // Tapped Allow but couldn't enable (e.g. iOS build without push support) -
      // back off so it doesn't immediately loop.
      snooze(); setOpen(false);
    }, 2000);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            className="bg-safaricab-card w-full max-w-sm rounded-2xl p-6 border border-white/10 shadow-2xl"
          >
            <div className="w-14 h-14 rounded-2xl bg-safaricab-gold/15 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-7 h-7 text-safaricab-gold" />
            </div>
            <h2 className="text-white text-lg font-bold text-center mb-2">{t('enable_push_prompt_title')}</h2>
            <p className="text-white/60 text-sm text-center mb-5">{t('enable_push_prompt_body')}</p>

            {denied && (
              <p className="text-red-400 text-xs text-center mb-4">{t('enable_push_prompt_denied')}</p>
            )}

            <button
              onClick={enable}
              disabled={enabling}
              className="w-full bg-safaricab-gold text-black font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {enabling ? <Loader className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              {t('enable_push_prompt_cta')}
            </button>
            <button
              onClick={() => { snooze(); setOpen(false); }}
              disabled={enabling}
              className="w-full mt-2 text-white/50 text-sm py-2"
            >
              {t('enable_push_prompt_later')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnableNotificationsPrompt;
